/* Native, scroll-snap carousels; existing Swiper HTML is retained. */
const ready = fn => document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, {once:true}) : fn();

function setupCarousel(element) {
  if (element.dataset.nativeCarouselReady) return;
  const track = element.querySelector('.swiper-container, .swiper-cards-carousel, .swiper-cards-banner-vertical');
  const wrapper = track?.querySelector('.swiper-wrapper');
  if (!track || !wrapper) return;
  element.dataset.nativeCarouselReady = 'true';
  const sectionId = track.dataset.id;
  const next = element.querySelector('.swiper-button-next') || document.querySelector(`.swiper-button-next--${CSS.escape(sectionId || 'none')}`);
  const previous = element.querySelector('.swiper-button-prev') || document.querySelector(`.swiper-button-prev--${CSS.escape(sectionId || 'none')}`);
  const root = element.closest('.card-carousel-parent-js') || element;
  const gap = Number(element.dataset.swiperGap) || 0;
  const desktopGap = Number(element.dataset.swiperGapDesktop) || gap;
  const mobileSlides = Number(element.dataset.swiperSlidesPerView) || 0;
  const desktopSlides = Number(element.dataset.swiperSlidesPerViewDesktop) || 0;
  const isVerticalBanner = track.classList.contains('swiper-cards-banner-vertical');
  // These sections were designed as grids, not sliders. Respect their existing CSS.
  // Article cards are a 4-column grid on desktop, but a slider on mobile.
  const isGridLayout = () => /(?:trending|discovery)_grid/.test(element.className) ||
    (element.classList.contains('card-carousel__products--articles_grid') && window.innerWidth >= 990);
  const isStaticDesktop = element.classList.contains('cards-banner__inner--desktop-static');
  const snapMode = element.hasAttribute('data-swiper-freemode') ? 'x proximity' : 'x mandatory';
  const clearNativeStyles = () => {
    for (const prop of ['display','overflow-x','overflow-y','scroll-snap-type','scroll-behavior','overscroll-behavior-inline','scrollbar-width','touch-action','gap','transform']) wrapper.style.removeProperty(prop);
    [...wrapper.children].forEach(slide => {
      for(const prop of ['scroll-snap-align','flex-shrink','flex-basis','flex']) slide.style.removeProperty(prop);
    });
    track.classList.remove('alternix-native-carousel');
    if (next) next.disabled = false;
    if (previous) previous.disabled = false;
  };
  const setup = () => {
    const desktop = window.innerWidth >= 1360;
    const wide = window.innerWidth >= 990;
    if (isGridLayout() || (isVerticalBanner && wide) || (isStaticDesktop && wide)) {
      clearNativeStyles();
      return;
    }
    const isMobileArticles = element.classList.contains('card-carousel__products--articles_grid') && !wide;
    const count = isVerticalBanner ? (window.innerWidth >= 640 ? 2.5 : window.innerWidth >= 480 ? 2.2 : 1.5)
      : isMobileArticles ? 1.12 : desktop ? desktopSlides : mobileSlides;
    wrapper.style.display = 'flex';
    wrapper.style.overflowX = 'auto';
    wrapper.style.overflowY = 'hidden';
    wrapper.style.scrollSnapType = snapMode;
    wrapper.style.scrollBehavior = 'smooth';
    wrapper.style.overscrollBehaviorInline = 'contain';
    wrapper.style.scrollbarWidth = 'none';
    wrapper.style.touchAction = 'pan-x pan-y';
    wrapper.style.gap = `${wide ? desktopGap : gap}px`;
    wrapper.style.setProperty('transform','none','important');
    const slides = [...wrapper.children];
    slides.forEach(slide => {
      slide.style.scrollSnapAlign = 'start';
      slide.style.flexShrink = '0';
      if (count) slide.style.flexBasis = `calc((100% - ${(count-1)*(wide?desktopGap:gap)}px) / ${count})`;
      else slide.style.removeProperty('flex-basis');
    });
    track.classList.add('alternix-native-carousel');
    updateControls();
  };
  const visible = () => [...wrapper.children].filter(s => getComputedStyle(s).display !== 'none' && !s.classList.contains('card--hidden-js'));
  const advance = sign => {
    if (!track.classList.contains('alternix-native-carousel')) return;
    const slides = visible();
    if (!slides.length) return;
    const viewportLeft = wrapper.getBoundingClientRect().left;
    const scrollLeft = wrapper.scrollLeft;
    const positions = slides.map(slide =>
      scrollLeft + slide.getBoundingClientRect().left - viewportLeft
    );
    const tolerance = 3;
    const target = sign > 0
      ? positions.find(position => position > scrollLeft + tolerance)
      : [...positions].reverse().find(position => position < scrollLeft - tolerance);
    if (target !== undefined) {
      wrapper.scrollTo({left: target, behavior: 'smooth'});
    }
  };
  // Coalesce scroll, resize and setup notifications into a single layout read per frame.
  let controlsFrame = 0;
  function updateControls() {
    if (controlsFrame) return;
    controlsFrame = requestAnimationFrame(() => {
      controlsFrame = 0;
      const scrollWidth = wrapper.scrollWidth;
      const clientWidth = wrapper.clientWidth;
      const scrollLeft = wrapper.scrollLeft;
      const overflowing = scrollWidth > clientWidth + 2;
      track.classList.toggle('swiper-no-overflow', !overflowing);
      if (next) next.disabled = !overflowing || scrollLeft >= scrollWidth - clientWidth - 3;
      if (previous) previous.disabled = !overflowing || scrollLeft <= 3;
    });
  }
  next?.addEventListener('click', e => {e.preventDefault();advance(1);});
  previous?.addEventListener('click', e => {e.preventDefault();advance(-1);});
  wrapper.addEventListener('scroll', updateControls, {passive:true});
  // Free-mode mouse dragging. Avoid snap-back and native text/image dragging.
  let drag = null;
  let suppressClick = false;
  let resetClickTimer = 0;
  let settleTimer = 0;
  wrapper.addEventListener('dragstart', e => {
    if (drag) e.preventDefault();
  });
  wrapper.addEventListener('pointerdown', e => {
    if (e.pointerType !== 'mouse' || e.button !== 0 || !track.classList.contains('alternix-native-carousel')) return;
    if (e.target.closest('button, input, select, textarea, [contenteditable]')) return;
    clearTimeout(settleTimer);
    drag = { x: e.clientX, start: wrapper.scrollLeft, moved: false };
    wrapper.style.scrollBehavior = 'auto';
    wrapper.style.scrollSnapType = 'none';
  });
  window.addEventListener('pointermove', e => {
    if (!drag) return;
    const dx = e.clientX - drag.x;
    if (Math.abs(dx) > 3) drag.moved = true;
    if (drag.moved) {
      e.preventDefault();
      wrapper.scrollLeft = drag.start - dx;
    }
  }, { passive: false });
  const stopDrag = () => {
    if (!drag) return;
    suppressClick = drag.moved;
    drag = null;
    // Keep snap disabled briefly so the browser does not jump backwards on release.
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      if (!drag) {
        wrapper.style.scrollSnapType = snapMode;
        wrapper.style.scrollBehavior = 'smooth';
      }
    }, 120);
    clearTimeout(resetClickTimer);
    resetClickTimer = setTimeout(() => { suppressClick = false; }, 180);
    updateControls();
  };
  window.addEventListener('pointerup', stopDrag);
  window.addEventListener('pointercancel', stopDrag);
  wrapper.addEventListener('click', e => {
    if (suppressClick) {
      e.preventDefault();
      e.stopPropagation();
      suppressClick = false;
    }
  }, true);
  new ResizeObserver(updateControls).observe(track);
  window.addEventListener('resize', setup, {passive:true});
  setup();
  // Theme Editor may insert or remove blocks without a full page reload.
  new MutationObserver(() => {setup();updateControls();}).observe(wrapper,{childList:true,subtree:false,attributes:true,attributeFilter:['class']});
}
function setupCardFilters(parent) {
  if (parent.dataset.nativeFiltersReady) return;
  parent.dataset.nativeFiltersReady='true';
  const tabs=[...parent.querySelectorAll('.card-carousel-tag-js')];
  const cards=[...parent.querySelectorAll('.card-carousel-js')];
  function select(tab) {
    tabs.forEach(t => { t.classList.toggle('button--tertiary--active',t===tab);t.removeAttribute('aria-selected');t.setAttribute('aria-pressed',String(t===tab)); });
    cards.forEach(card => {card.classList.toggle('card--hidden-js',card.dataset.type!==tab.dataset.type);card.style.removeProperty('display');});
    const wrapper=parent.querySelector('.swiper-wrapper');if(wrapper){wrapper.scrollLeft=0;wrapper.dispatchEvent(new Event('scroll'));requestAnimationFrame(()=>wrapper.dispatchEvent(new Event('scroll')));}
  }
  tabs.forEach(tab=>tab.addEventListener('click',()=>select(tab)));
  if(tabs.length) select(tabs[0]);
  parent.querySelectorAll('.js-card-double-sided').forEach(card=>card.addEventListener('click',event=>{
    const side=card.querySelector('.js-card-double-sided-bottom');
    if(side)side.toggleAttribute('flipped');
  }));
}
function init(scope=document) {
  scope.querySelectorAll('.card-carousel-parent-js').forEach(setupCardFilters);
  scope.querySelectorAll('swiper-component').forEach(setupCarousel);
  scope.querySelectorAll('.swiper-cards-banner-vertical').forEach(el => {
    if(!el.closest('swiper-component'))setupCarousel(el.parentElement);
  });
}
ready(()=>init());
document.addEventListener('shopify:section:load',e=>init(e.target));
